import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Mail, MapPin, FileText, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import DownloadGateModal from "@/components/DownloadGateModal";
import { useLang } from "@/i18n/LanguageContext";

function FooterNewsletter() {
  const { lang } = useLang();
  const en = lang === "en";
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, first_name: "" });
      toast.success(data.message || "Abonnement confirmé");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-12 py-10 border-t border-fg/10" data-testid="footer-newsletter">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-6">
          <div className="meta-label text-cinabre">§ Newsletter · {en ? "The Thread" : "Le Fil"}</div>
          <div className="mt-2 font-serif text-2xl md:text-3xl leading-tight tracking-tight">
            {en ? <>Stay in <em className="italic">the thread</em> — openings, stops, calls for artists.</> : <>Rester dans <em className="italic">le fil</em> — vernissages, escales, appels à artistes.</>}
          </div>
        </div>
        <form onSubmit={onSubmit} className="md:col-span-6 flex gap-3 items-end" data-testid="footer-newsletter-form">
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.fr"
            className="flex-1 bg-transparent border-b border-fg/30 focus:border-cinabre outline-none py-3 font-serif text-xl transition-colors"
            data-testid="footer-newsletter-email"
          />
          <button
            type="submit"
            disabled={loading}
            className="shrink-0 inline-flex items-center gap-2 bg-cinabre text-bg px-6 py-3 font-mono text-[10px] tracking-[0.24em] uppercase hover:bg-fg transition-colors disabled:opacity-50"
            data-testid="footer-newsletter-submit"
          >
            {loading ? (en ? "Sending…" : "Envoi…") : (en ? "Subscribe" : "S'inscrire")} <ArrowRight size={13} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Footer() {
  const { lang } = useLang();
  const en = lang === "en";
  const [dl, setDl] = useState({ open: false, doc: "statuts" });
  const openDl = (doc) => setDl({ open: true, doc });

  return (
    <footer className="mt-32 border-t border-fg/15 bg-bg" data-testid="site-footer">
      <div className="px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-start gap-6 md:gap-8">
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/0zqwmc4d_logo%20Fil%20du%20Midi.png"
              alt="FILM · Le Fil du Midi"
              className="h-28 md:h-36 lg:h-40 w-auto object-contain shrink-0"
              data-testid="film-logo-footer"
            />
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/z5ei020t_ARTEURS_NB.webp"
              alt="Les Arteurs · Art Maker"
              className="h-20 md:h-28 lg:h-32 w-auto object-contain shrink-0 mt-2"
              data-testid="arteurs-logo"
            />
          </div>
          <div className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight mt-10">
            {en ? <>From the bank<br/>to the <em className="italic">gallery</em>.</> : <>De la berge<br/>à la <em className="italic">galerie</em>.</>}
          </div>
          <p className="mt-6 max-w-md text-fg/70 leading-relaxed">
            {en
              ? "Les Arteurs — an association committed to promoting contemporary art, from the Atlantic to the Mediterranean. We do not break down walls: we open doors."
              : "Les Arteurs — une association engagée dans la promotion de l'art contemporain, de l'Atlantique à la Méditerranée. Nous ne cassons pas les murs : nous ouvrons les portes."}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="meta-label mb-6 text-fg/60">{en ? "Navigate" : "Naviguer"}</div>
          <ul className="space-y-3 font-serif text-xl">
            <li><Link to="/parcours" className="link-underline">{en ? "The 6 sites" : "Les 6 sites"}</Link></li>
            <li><Link to="/artistes" className="link-underline">{en ? "The artists" : "Les artistes"}</Link></li>
            <li><Link to="/parcours" className="link-underline">{en ? "The 750 km trail" : "Le parcours 750 km"}</Link></li>
            <li><Link to="/partenaires" className="link-underline">{en ? "Partners" : "Partenaires"}</Link></li>
            <li><Link to="/mecenat" className="link-underline">{en ? "Patronage" : "Mécénat"}</Link></li>
            <li><Link to="/contact" className="link-underline">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4 space-y-8">
          <div>
            <div className="meta-label mb-6 text-fg/60">Les Arteurs</div>
            <ul className="space-y-4 text-fg/80">
              <li className="flex items-start gap-3"><MapPin size={16} className="mt-1 shrink-0" /><span>40 Grande Rue, 31620 Castelnau d'Estrétefonds</span></li>
              <li className="flex items-center gap-3"><Mail size={16} /><a href="mailto:arteurs.lecollectif@gmail.com" className="link-underline">arteurs.lecollectif@gmail.com</a></li>
              <li className="flex items-center gap-3"><Instagram size={16} /><a href="https://les-arteurs.org" target="_blank" rel="noreferrer" className="link-underline">les-arteurs.org</a></li>
            </ul>
          </div>

          <div>
            <div className="meta-label mb-4 text-fg/60">{en ? "Official documents" : "Documents officiels"}</div>
            <div className="flex flex-col gap-2" data-testid="footer-docs">
              <button
                type="button"
                onClick={() => openDl("statuts")}
                className="inline-flex items-center gap-2 text-sm text-fg/80 hover:text-cinabre transition-colors group text-left"
                data-testid="footer-dl-statuts"
              >
                <FileText size={14} className="shrink-0" />
                <span className="link-underline">{en ? "Association statutes" : "Statuts de l'association"}</span>
                <Download size={12} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <button
                type="button"
                onClick={() => openDl("joafe")}
                className="inline-flex items-center gap-2 text-sm text-fg/80 hover:text-cinabre transition-colors group text-left"
                data-testid="footer-dl-joafe"
              >
                <FileText size={14} className="shrink-0" />
                <span className="link-underline">Déclaration JOAFE</span>
                <Download size={12} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <p className="mt-2 font-mono text-[9px] tracking-[0.22em] uppercase text-fg/40">
                {en ? "Download after identification." : "Téléchargement sur identification préalable."}
              </p>
            </div>
          </div>

          <div>
            <div className="meta-label mb-3 text-fg/60">{en ? "Legal identification" : "Identification légale"}</div>
            <ul className="space-y-1 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">
              <li>N° RNA · <span className="text-fg/80">W313039555</span></li>
              <li>N° SIRET · <span className="text-fg/80">992 816 124 00018</span></li>
            </ul>
          </div>
        </div>
      </div>

      <FooterNewsletter />

      <div className="px-6 md:px-12 py-6 border-t border-fg/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50">
        <div>© 2026 Les Arteurs · {en ? "All rights reserved" : "Tous droits réservés"}</div>
        <div>Le Fil du Midi — FILM · Canal'Art 2026 · {en ? "30 years UNESCO" : "30 ans UNESCO"}</div>
      </div>

      <DownloadGateModal
        open={dl.open}
        onClose={() => setDl({ ...dl, open: false })}
        document={dl.doc}
      />
    </footer>
  );
}
